import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import ky from 'ky'

import { API_PREFIX } from '../../config'
import { logEvents } from '../../copilot/logEvents'
import {
  type RequestError,
  extractHttpError,
  stripQueryString,
} from './extractHttpError'

export type Config = {
  waitForConnectionBeforeShowingSummary: boolean
  debugLogging: boolean
}

//documentation can be found here:
// https://api.capturi.ai/puzzel-widget/documentation/swagger
export const useConfig = (
  apiToken: string | null,
): UseQueryResult<Config, RequestError> =>
  useQuery({
    enabled: !!apiToken,
    queryKey: [apiToken, 'config'],
    queryFn: async () => {
      try {
        return await ky
          .get(`${API_PREFIX}/puzzel-widget/config`, {
            credentials: 'include',
            mode: 'cors',
            headers: {
              Authorization: apiToken || undefined,
            },
          })
          .json<Config>()
      } catch (error) {
        const e = await extractHttpError(error)

        logEvents({
          eventType: 'useConfig error',
          level: 'error',
          href: stripQueryString(window.location.href),
          name: e.name,
          message: e.message,
          ...e.cause,
        })

        return {
          waitForConnectionBeforeShowingSummary: true,
          debugLogging: false,
        }
      }
    },
  })

export const getConfig = async (apiToken: string): Promise<Config> => {
  try {
    return await ky
      .get(`${API_PREFIX}/puzzel-widget/config`, {
        credentials: 'include',
        retry: 10,
        mode: 'cors',
        headers: {
          Authorization: apiToken || undefined,
        },
      })
      .json<Config>()
  } catch (error) {
    const e = await extractHttpError(error)
    logEvents({
      eventType: 'getConfig error',
      level: 'error',
      href: stripQueryString(window.location.href),
      name: e.name,
      message: e.message,
      ...e.cause,
    })

    return {
      waitForConnectionBeforeShowingSummary: true,
      debugLogging: false,
    }
  }
}
